import { SnowflakeSyncDataDialog } from '@/components/Dialogs/SnowflakeSyncDataDialog';
import SnowflakeSyncIndicator from '@/components/Layout/components/Header/SnowflakeSyncIndicator';
import { PredictContextProvider } from '@/components/Predict/predictContext';
import SnowflowIcon from '@/images/data-browser/snowflake-transparent.png';
import { useCurrentProjectModelInfoQuery, useModelStatusQuery } from '@/serverStore/projectModels';
import { useGetSelectedProjectQuery, useUpdateProjectMutation } from '@/serverStore/projects';
import { createSnapshotDialogModeAtom } from '@/uiStates/datasetSnapshot/pageUIStates';
import { initLabelTogglesAtom } from '@/uiStates/mediaDetails/labelToggles';
import {
  annotationInstanceAtom,
  datasetSearchParamsAtom,
} from '@/uiStates/mediaDetails/pageUIStates';
import {
  ApiResponseLoader,
  BottomDrawer,
  Button,
  IconButton,
  Pagination,
  useStateSyncSearchParams,
} from '@clef/client-library';
import {
  AnnotationInstance,
  DatasetGroupOptions,
  LabelType,
  Media,
  MediaId,
  MediaStatusType,
  ModelStatus,
  UserPermission,
} from '@clef/shared/types';
import { Box, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import BarChartIcon from '@material-ui/icons/BarChart';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import { default as classNames, default as cx } from 'classnames';
import { useAtom, useSetAtom } from 'jotai';
import { omit } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { useDialog } from '../../components/Layout/components/useDialog';
import ManageSplitDialog from '../../components/StatsCard/ManageSplitDialog';
import { useWorkflowAssistantState } from '../../components/WorkflowAssistant/state';
import CLEF_PATH from '../../constants/path';
import {
  ClientFeatures,
  useFastNEasyEnabled,
  useFeatureGateEnabled,
} from '../../hooks/useFeatureGate';
import { useHasPermission } from '../../hooks/useProjectRolePermissions';
import { isModelTrainingSuccessful } from '../../store/projectModelInfoState/utils';
import { StepName } from '../../types/client';
import { CreateSnapshotDialog } from './CreateSnapshotDialog';
import {
  appliedFilterMappingToFormattedFilterMapping,
  DataBrowserStateContext,
  getColumnFilterMapWithModelId,
  getSelectedMediaCount,
  getStateFromStorage,
  saveStateToStorage,
} from './dataBrowserState';
import DataBrowserUploadDropZone from './DataBrowserUploadDropZone';
import { DataSummaryDialog } from './DataSummaryDialog';
import FREEmptyPage from './FREEmptyPage/FREEmptyPage';
import InstanceGrid from './InstanceGrid';
import LabelControlBar from './LabelControlBar';
import MediaGrid from './MediaGrid/MediaGrid';
import MediaSelectionActions from './MediaSelectionActions';
import ModelPerformance from './ModelPerformance';
import ModelList from './ModelPerformance/ModelList';
import MoreButton from './MoreButton';
import SnapshotActionButton from './SnapshotActionButton';
import { SidebarWidthV2, useDataBrowserStyles } from './styles';
import Toolbar from './Toolbar';
import TrainModelButtonGroup from './TrainModelButtonGroup';
import {
  useAnnotationInstances,
  useDatasetMedias,
  useTotalMediaCount,
  useTotalMediaOrInstanceCount,
} from './utils';
import { useGetDatasetStatsQuery } from '@/serverStore/dataset/queries';
import { useDefectSelector } from '@/store/defectState/actions';
import { AnomalyDefectionClassName } from '@clef/shared/constants';
import { Alert } from '@material-ui/lab';

const FREDataBrowser: React.FC<{}> = () => {
  const styles = useDataBrowserStyles();
  const history = useHistory();

  const { id: projectId, datasetId, labelType } = useGetSelectedProjectQuery().data ?? {};
  const [state, dispatch] = useImmer(omit(getStateFromStorage(projectId), 'labelDisplay'));
  const [dataInsightsDialogOpen, setDataInsightsDialogOpen] = useState(false);

  const { appliedFilters, rightSidebar, viewMode, sortField } = state;
  const totalMediaOrInstanceCount = useTotalMediaOrInstanceCount(state);
  const [filterSearchParam, setFilterSearchParam] = useStateSyncSearchParams('filters', '');
  // initialPageIndexSearchParam is used for restoring page index when returning from media_details page
  const [initialPageIndexSearchParam, setInitialPageIndexSearchParam] = useStateSyncSearchParams(
    'initialPageIndex',
    '',
  );
  const [isManageSplitDialogOpen, setManageSplitDialogOpen] = useState(false);
  const {
    state: { goButtonClicked, step },
    dispatch: workflowAssitantDispatch,
  } = useWorkflowAssistantState();
  const { enqueueSnackbar } = useSnackbar();
  const [openSnowflowSyncDialog, setOpenSnowflowSyncDialog] = useState(false);

  const initLabelToggles = useSetAtom(initLabelTogglesAtom);
  const setDatasetSearchParams = useSetAtom(datasetSearchParamsAtom);
  const setAnnotationInstance = useSetAtom(annotationInstanceAtom);
  const enableSnowflakeSync = useFeatureGateEnabled(ClientFeatures.SnowflakeSync);

  const [createSnapshotDialogMode] = useAtom(createSnapshotDialogModeAtom);

  useEffect(() => {
    try {
      const filterDecoded = JSON.parse(decodeURIComponent(filterSearchParam));
      dispatch(draft => {
        draft.appliedFilters = filterDecoded;
      });
    } catch (e) {
      setFilterSearchParam(encodeURIComponent(JSON.stringify({})));
    }
    // only do this on mount, afterwards appliedFilters can be only changed from user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const pageIndexDecoded = JSON.parse(decodeURIComponent(initialPageIndexSearchParam));
      if (pageIndexDecoded) {
        dispatch(draft => {
          draft.pageIndex = pageIndexDecoded;
        });
      }
    } catch (e) {
      setInitialPageIndexSearchParam('');
    }
    // Note: only do this on mount, afterwards page index can be only changed from user
    // do not add initialPageIndexSearchParam and setInitialPageIndexSearchParam to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: totalMediaCount } = useTotalMediaCount();
  // Store total media count
  useEffect(() => {
    if (totalMediaCount) {
      dispatch(draft => {
        draft.totalMediaCount = totalMediaCount;
      });
    }
  }, [dispatch, totalMediaCount]);

  const selectedMediaCount = totalMediaOrInstanceCount ? getSelectedMediaCount(state) : 0;

  // On appliedFilters change, do 2 things
  // 1. Reset selected media when filters change
  // 2. Save filters to FilterSearchParam
  useEffect(() => {
    dispatch(draft => {
      draft.selectingMediaMode = 'select';
      draft.selectedOrUnselectedMedia = [];
    });
    setFilterSearchParam(encodeURIComponent(JSON.stringify(appliedFilters)));
  }, [dispatch, appliedFilters, setFilterSearchParam]);

  // save to storage
  useEffect(() => {
    projectId && saveStateToStorage(projectId, state);
  }, [projectId, state]);

  // Reset pageIndex to 0 when filter or sortField or mode changed
  useEffect(() => {
    // remove initialPageIndex in url after use
    if (initialPageIndexSearchParam) {
      setInitialPageIndexSearchParam('');
    } else {
      dispatch(draft => {
        draft.pageIndex = 0;
      });
    }
    // Note: initialPageIndex should only be set when the page is first loaded
    // and should not be set when the page is reloaded,
    // do not add initialPageIndexSearchParam and setInitialPageIndexSearchParam to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, dispatch, sortField, viewMode]);

  const { id: currentModelId } = useCurrentProjectModelInfoQuery();
  const { data: modelStatusRes } = useModelStatusQuery(projectId, currentModelId);
  const { status: modelStatus, metricsReady } = modelStatusRes ?? {};
  const fastEasyModelSuccessful = isModelTrainingSuccessful(modelStatus, metricsReady);
  const isOfflinePredicted = modelStatus === ModelStatus.OfflinePredicted;
  // In any case, if there is no successful model, we need to fall back to show ground truth in data browser
  useEffect(() => {
    dispatch(draft => {
      if (viewMode === 'instance') {
        draft.showGroundTruth = true;
        draft.showPredictions = false;
      } else {
        if (
          labelType &&
          [LabelType.AnomalyDetection, LabelType.Classification].includes(labelType)
        ) {
          if (fastEasyModelSuccessful) {
            draft.showPredictions = true;
            draft.showGroundTruth = false;
          } else {
            draft.showGroundTruth = true;
            draft.showPredictions = false;
          }
        } else {
          draft.showGroundTruth = true;
          draft.showPredictions = !!fastEasyModelSuccessful || isOfflinePredicted;
        }
      }
    });
  }, [dispatch, modelStatus, labelType, fastEasyModelSuccessful, viewMode, isOfflinePredicted]);

  const {
    data: datasetMedias,
    isLoading: datasetMediasLoading,
    error: datasetMediasError,
  } = useDatasetMedias(state);
  const {
    data: annotationInstances,
    isLoading: annotationInstancesLoading,
    error: annotationInstancesError,
  } = useAnnotationInstances(state);

  const [mediasOrInstances, mediasOrInstancesLoading, mediasOrInstancesError] = useMemo(() => {
    return viewMode === 'image'
      ? [datasetMedias, datasetMediasLoading, datasetMediasError]
      : [annotationInstances, annotationInstancesLoading, annotationInstancesError];
  }, [
    viewMode,
    datasetMedias,
    datasetMediasLoading,
    datasetMediasError,
    annotationInstances,
    annotationInstancesLoading,
    annotationInstancesError,
  ]);

  const canUploadData = useHasPermission(UserPermission.UploadData);
  const canTrainModel = useHasPermission(UserPermission.TrainModel);
  const {
    data: mediaDefectDistribution,
    isLoading: mediaDefectDistributionLoading,
    error: mediaDefectDistributionError,
  } = useGetDatasetStatsQuery({
    selectOptions: undefined,
    groupOptions: [DatasetGroupOptions.DEFECT_DISTRIBUTION],
    // version,
  });
  const allDefects = useDefectSelector();
  const defectIdToName = useMemo(() => {
    return allDefects.reduce((acc, defect) => {
      acc[defect.id] = defect.name;
      return acc;
    }, {} as Record<number, string>);
  }, [allDefects]);

  const adProjectHasNoAbnormalImages =
    labelType === LabelType.AnomalyDetection &&
    !mediaDefectDistributionLoading &&
    !mediaDefectDistributionError &&
    mediaDefectDistribution?.length === 1 &&
    mediaDefectDistribution?.[0]?.defect_distribution &&
    defectIdToName[parseInt(Object.keys(mediaDefectDistribution[0].defect_distribution)?.[0])] ===
      AnomalyDefectionClassName.Normal;

  const { openUpload } = useDialog();
  const fastNEasySupported = useFastNEasyEnabled();

  // below are assembling the parameters related to media details page
  const [columnFilterMap, metadataFilterMap] = useMemo(() => {
    const [col, metadata] = appliedFilterMappingToFormattedFilterMapping(appliedFilters);
    const colWithModelId = getColumnFilterMapWithModelId(col, currentModelId);
    return [colWithModelId, metadata];
  }, [appliedFilters, currentModelId]);

  const listParamsForDetailsPage = useMemo(() => {
    return {
      sortOptions: {
        ...sortField,
        offset: state.pageIndex * state.paginationLimit,
        limit: state.paginationLimit,
      },
      columnFilterMap,
      metadataFilterMap: Object.fromEntries(
        Object.entries(metadataFilterMap).filter(([, value]) => {
          return Object.values(value).some(subValue => {
            return Array.isArray(subValue) ? subValue.length > 0 : !!subValue;
          });
        }),
      ),
    };
  }, [sortField, state.pageIndex, state.paginationLimit, columnFilterMap, metadataFilterMap]);

  // If go button in workflow assistant is clicked, and the current step is label, then open the media details dialog,
  // and mark go button unclicked
  useEffect(() => {
    if (goButtonClicked && step?.stepName === StepName.Label && datasetMedias) {
      const firstUnlabeledMedia = datasetMedias?.find(
        media => media.mediaStatus === MediaStatusType.Raw,
      );

      if (firstUnlabeledMedia) {
        initLabelToggles({ showHeatmap: state.showHeatmap });
        setDatasetSearchParams(listParamsForDetailsPage);
        history.push(
          `${CLEF_PATH.data.mediaDetails}/${firstUnlabeledMedia.id}${
            currentModelId ? `?modelId=${currentModelId}` : ''
          }`,
        );

        workflowAssitantDispatch(state => {
          state.goButtonClicked = false;
        });
      } else {
        enqueueSnackbar('All images were labeled. Please upload more images to continue labeling', {
          variant: 'warning',
        });
      }
    }
  }, [
    datasetMedias,
    enqueueSnackbar,
    goButtonClicked,
    step?.stepName,
    workflowAssitantDispatch,
    history,
    listParamsForDetailsPage,
    initLabelToggles,
    state.showHeatmap,
    setDatasetSearchParams,
    currentModelId,
  ]);

  const updateProject = useUpdateProjectMutation();
  const [openTootip, setOpenToolTip] = useState(false);

  const moreButtonCommonAttrs = {
    onMouseEnter: () => {
      setOpenToolTip(true);
    },
    onMouseLeave: () => {
      setOpenToolTip(false);
    },
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setOpenToolTip(false);
    },
  };

  if (!projectId) {
    return null;
  }

  return (
    <DataBrowserStateContext.Provider value={{ state, dispatch }}>
      <PredictContextProvider
        projectId={projectId}
        datasetId={datasetId}
        labelType={labelType}
        modelId={currentModelId}
      >
        {!datasetMediasLoading &&
        // If there is error, leave the following normal logic to handle
        !datasetMediasError &&
        !datasetMedias?.length &&
        !Object.entries(appliedFilters).length &&
        viewMode === 'image' ? (
          <Box paddingRight={rightSidebar === 'model_performance' ? SidebarWidthV2 + 'px' : 0}>
            <Box
              className={classNames(
                styles.topBar,
                styles.freTopBar,
                rightSidebar === 'model_performance' && 'sideBarV2Opened',
              )}
            >
              <div className={styles.topActionButton}>
                <Tooltip title={t('More actions')} open={openTootip} placement="top" arrow>
                  <Box {...moreButtonCommonAttrs}>
                    <MoreButton onlyClassesAndMetadata />
                  </Box>
                </Tooltip>
                {enableSnowflakeSync && (
                  <>
                    <Button
                      id="snowflake-stage-button"
                      data-testid="data-browser-snowflake-stage-button"
                      variant="outlined"
                      startIcon={<img src={SnowflowIcon} width={20} height={20} />}
                      onClick={() => setOpenSnowflowSyncDialog(true)}
                    >
                      <Typography>{t('Sync Snowflake Data')}</Typography>
                    </Button>
                    <SnowflakeSyncIndicator />
                  </>
                )}
              </div>
            </Box>

            <FREEmptyPage
              changeLabelType={async newLabelType => {
                if (newLabelType === labelType) {
                  return;
                }
                updateProject.mutate(
                  {
                    id: projectId,
                    labelType: newLabelType,
                  },
                  {
                    onError: e =>
                      enqueueSnackbar((e as Error).message, {
                        variant: 'error',
                        autoHideDuration: 12000,
                      }),
                  },
                );
              }}
              changingLabelType={updateProject.isLoading}
              withRightPanel={rightSidebar === 'model_performance'}
            />
            <ModelPerformance />
            {enableSnowflakeSync && openSnowflowSyncDialog && (
              <SnowflakeSyncDataDialog
                showClassificationToggle={labelType === LabelType.Classification}
                open={openSnowflowSyncDialog}
                onClose={() => setOpenSnowflowSyncDialog(false)}
              />
            )}
          </Box>
        ) : (
          <>
            <DataBrowserUploadDropZone
              disabled={state.isFirstRunLiveExperienceModalOpen || !canUploadData}
              className={cx({
                [styles.dataBrowserContentWrapper]: true,
                [styles.customizedScrollbar]: true,
                [styles.wrapperWithMR]: rightSidebar === 'model_performance',
              })}
            >
              <Box mr={rightSidebar === 'model_performance' ? 5 : 0}>
                {/*  Bottom Drawer - Media Selection Actions */}
                <BottomDrawer
                  show={!!selectedMediaCount}
                  className={cx(
                    styles.scrollStickyContainer,
                    styles.newTopDrawer,
                    rightSidebar && styles.shrinkedBottomDrawerWidthV2,
                  )}
                >
                  <MediaSelectionActions
                    style={{
                      width: `calc(80vw - 400px - ${rightSidebar ? SidebarWidthV2 : 0}px)`,
                    }}
                  />
                </BottomDrawer>
                {adProjectHasNoAbnormalImages && (
                  <Alert className={styles.alerts} severity="info">
                    {t(
                      'Because there are no abnormal images, the model has no reference for comparison, so a default threshold will be used. This may impact performance. We recommend uploading a few abnormal examples to improve results.',
                    )}
                  </Alert>
                )}
                <Box
                  position="absolute"
                  top={28}
                  left={110}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography
                    variant="body1"
                    component="div"
                    className={styles.newMediaCountText}
                    data-testid={'databrowser-total-medias'}
                  >
                    {viewMode === 'image'
                      ? t('{{mediaCount}} images', { mediaCount: totalMediaOrInstanceCount })
                      : t('{{count}} instances', { count: totalMediaOrInstanceCount })}
                  </Typography>

                  <IconButton
                    id="data-summary-button"
                    test-dataid="data-summary-button"
                    size="small"
                    tooltip={t('Displays a summary of your labels and label distributions.')}
                    onClick={() => {
                      setDataInsightsDialogOpen(true);
                    }}
                  >
                    <BarChartIcon />
                  </IconButton>
                </Box>

                {/* Upload & Train */}
                <Box
                  justifyContent="space-between"
                  alignItems="center"
                  className={cx(
                    styles.topBar,
                    styles.freTopBar,
                    rightSidebar === 'model_performance' && 'sideBarV2Opened',
                  )}
                >
                  <div className={styles.topActionButton}>
                    <Tooltip open={openTootip} title={t('More actions')} placement="top" arrow>
                      <Box {...moreButtonCommonAttrs}>
                        <MoreButton />
                      </Box>
                    </Tooltip>
                    <SnapshotActionButton />
                    {canUploadData && (
                      <IconButton
                        id="upload-image-button"
                        onClick={() => openUpload()}
                        data-testid="data-browser-upload-btn"
                        className={styles.uploadIconButton}
                        tooltip={t('Select and upload images from a directory.')}
                      >
                        <CloudUploadOutlinedIcon className={styles.uploadIcon} />
                      </IconButton>
                    )}
                    {enableSnowflakeSync && (
                      <>
                        <Button
                          id="snowflake-stage-button"
                          data-testid="data-browser-snowflake-stage-button"
                          variant="outlined"
                          startIcon={<img src={SnowflowIcon} width={20} height={20} />}
                          onClick={() => setOpenSnowflowSyncDialog(true)}
                        >
                          <Typography>{t('Sync Snowflake Data')}</Typography>
                        </Button>
                        <SnowflakeSyncIndicator />
                      </>
                    )}
                    {fastNEasySupported && canTrainModel && <TrainModelButtonGroup />}
                  </div>
                  {enableSnowflakeSync && openSnowflowSyncDialog && (
                    <SnowflakeSyncDataDialog
                      showClassificationToggle={labelType === LabelType.Classification}
                      open={openSnowflowSyncDialog}
                      onClose={() => setOpenSnowflowSyncDialog(false)}
                    />
                  )}
                </Box>
                {/*
                tool bar:
                Image/instance view, filter, sort, data insights, model performance
                Ground truth / prediction, select all, image size etc.
              */}
                <Toolbar />
                <LabelControlBar />
                {/* media grid */}
                <ApiResponseLoader
                  response={mediasOrInstances}
                  loading={mediasOrInstancesLoading}
                  error={mediasOrInstancesError}
                  defaultHeight={500}
                >
                  {mediasOrInstances => {
                    return mediasOrInstances.length ? (
                      <Box position="relative">
                        {/* this progress is for loading with stale and refresh */}
                        {mediasOrInstancesLoading && (
                          <LinearProgress className={styles.loadingImagesProgress} />
                        )}
                        {viewMode === 'image' ? (
                          <MediaGrid
                            medias={mediasOrInstances as Media[]}
                            onInfoClick={(mediaId: MediaId) => {
                              initLabelToggles({ showHeatmap: state.showHeatmap });
                              setDatasetSearchParams(listParamsForDetailsPage);
                              history.push(
                                `${CLEF_PATH.data.mediaDetails}/${mediaId}${
                                  currentModelId ? `?modelId=${currentModelId}` : ''
                                }`,
                              );
                            }}
                            showClassChip
                          />
                        ) : (
                          <InstanceGrid
                            instances={mediasOrInstances as AnnotationInstance[]}
                            onInstanceClick={(instance: AnnotationInstance) => {
                              initLabelToggles({ showHeatmap: state.showHeatmap });
                              setDatasetSearchParams(listParamsForDetailsPage);
                              setAnnotationInstance(instance);
                              history.push(
                                `${CLEF_PATH.data.mediaDetails}/${instance.mediaId}${
                                  currentModelId ? `?modelId=${currentModelId}` : ''
                                }`,
                              );
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography
                        variant="subtitle1"
                        component="div"
                        gutterBottom
                        className={styles.hintTextNormal}
                        data-test-id="no-medias-found-data-browser"
                      >
                        {Object.entries(appliedFilters).length ? (
                          <>
                            {/* No media, non match filter condition */}
                            {viewMode === 'image' ? t('No image found') : t('No instance found')}
                            <Button
                              color="secondary"
                              size="small"
                              id="data-browser-empty-upload-button"
                              className={styles.textButton}
                              onClick={() =>
                                dispatch(draft => {
                                  draft.appliedFilters = {};
                                })
                              }
                            >
                              {t('Clear filters')}
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* No media, none uploaded */}
                            {t('No instance found')}
                            {canUploadData && (
                              <Button
                                id="empty-state-upload-image-button"
                                color="primary"
                                size="small"
                                className={styles.textButton}
                                onClick={() => openUpload()}
                              >
                                {t('Upload Media')}
                              </Button>
                            )}
                          </>
                        )}
                      </Typography>
                    );
                  }}
                </ApiResponseLoader>

                <Box marginTop={4}>
                  <Pagination
                    data-testid="databrowser-bottom-pagination"
                    count={Math.ceil((totalMediaOrInstanceCount ?? 0) / state.paginationLimit)}
                    color="primary"
                    size="small"
                    page={state.pageIndex + 1}
                    onPageChange={newPageIndex =>
                      dispatch(draft => {
                        draft.pageIndex = newPageIndex - 1;
                      })
                    }
                    className={styles.pagination}
                  />
                </Box>
                <Box height={16} />
              </Box>

              {/* Auto split dialog */}
              {/* {isAutoSplitDialogOpen && <AutoSplitDialog onClose={() => setAutoSplitDialogOpen(false)} />} */}
              {isManageSplitDialogOpen && (
                <ManageSplitDialog
                  onClose={() => {
                    setManageSplitDialogOpen(false);
                  }}
                />
              )}

              {createSnapshotDialogMode !== null && <CreateSnapshotDialog />}

              <DataSummaryDialog
                open={dataInsightsDialogOpen}
                onClose={() => {
                  setDataInsightsDialogOpen(false);
                }}
              />
            </DataBrowserUploadDropZone>
            {rightSidebar === 'model_performance' && <ModelList />}
          </>
        )}
      </PredictContextProvider>
    </DataBrowserStateContext.Provider>
  );
};

export default FREDataBrowser;
